/**
 * X-Road Tenant Type Definitions
 *
 * These types define the multi-tenant architecture for the X-Road Control Tower.
 * They mirror the backend enums from:
 * - backend/src/main/java/com/goflux/exposeapi/tenant/domain/TenantType.java
 * - backend/src/main/java/com/goflux/exposeapi/tenant/domain/HostingTier.java
 * - backend/src/main/java/com/goflux/exposeapi/tenant/domain/TenantMetadata.java
 *
 * Purpose: Support 3 hosting modes (SENUM Operator, Hosted, Self-Managed) with
 * different UI experiences and permission levels.
 */

// ============================================================================
// Tenant Type Enum
// ============================================================================

/**
 * Defines the type of X-Road member/organization and their hosting arrangement.
 *
 * This determines:
 * - Which portal UI they see (Operator Console vs Control Tower vs Managed Portal)
 * - What features are available
 * - Authorization levels
 * - Service publishing limits
 */
export enum TenantType {
  /**
   * SENUM - Sénégal Numérique (Central Authority)
   * - Manages the entire X-Road instance (SN-GOV)
   * - Can see all members, services, certificates
   * - Approves cross-ministry subscriptions
   * - Provisions hosted Security Servers
   * - Tracks revenue and billing
   *
   * Portal: Operator Console
   */
  CENTRAL_AUTHORITY = 'CENTRAL_AUTHORITY',

  /**
   * Large Ministry/Institution (e.g., Ministry of Finance, BCEAO)
   * - Owns and manages their own Security Server(s)
   * - Full control over subsystems and services
   * - Can create sub-tenants for departments
   * - Advanced features enabled
   *
   * Portal: Control Tower (full features)
   */
  MEMBER_SELF_MANAGED = 'MEMBER_SELF_MANAGED',

  /**
   * Medium Agency with SENUM-hosted dedicated Security Server
   * - SENUM manages their Security Server
   * - Can publish services up to tier limits
   * - Simplified UI (no SS management)
   * - Pays monthly hosting fee
   *
   * Portal: Managed Portal
   */
  MEMBER_HOSTED = 'MEMBER_HOSTED',

  /**
   * Small Agency/NGO/Startup with shared Security Server
   * - Shares Security Server with other light tenants
   * - Limited to 5 services, 10K API calls/month
   * - Entry-level tier (50K FCFA/month)
   * - Simplified UI with upgrade prompts
   *
   * Portal: Light Portal (limited features)
   */
  MEMBER_HOSTED_LIGHT = 'MEMBER_HOSTED_LIGHT',

  /**
   * Autonomous Subsystem under a self-managed member
   * - Has own services and API keys
   * - Uses parent organization's Security Server
   * - Independent admin access
   * - Example: ARMP under Ministry of Finance
   *
   * Portal: Control Tower (subsystem view)
   */
  SUBSYSTEM_HOSTED = 'SUBSYSTEM_HOSTED',

  /**
   * Internal Subsystem (department/division)
   * - Limited autonomy
   * - Managed by parent organization
   * - Example: Direction du Budget under Ministry of Finance
   *
   * Portal: Limited Control Tower
   */
  SUBSYSTEM_INTERNAL = 'SUBSYSTEM_INTERNAL',
}

// ============================================================================
// Hosting Tier Enum
// ============================================================================

/**
 * Hosting tiers for MEMBER_HOSTED and MEMBER_HOSTED_LIGHT tenants.
 * Determines service limits, API call quotas, and monthly fees.
 *
 * Pricing structure (Senegal market):
 * - LIGHT: 50,000 FCFA/month (~$85)
 * - STANDARD: 100,000 FCFA/month (~$170)
 * - MEDIUM: 200,000 FCFA/month (~$340)
 * - LARGE: 400,000 FCFA/month (~$680)
 */
export enum HostingTier {
  /**
   * Entry-level tier for small organizations
   * - Max 5 services
   * - 10,000 API calls/month
   * - Shared Security Server
   * - 50K FCFA/month
   */
  LIGHT = 'LIGHT',

  /**
   * Standard tier for medium agencies
   * - Max 20 services
   * - 100,000 API calls/month
   * - Dedicated or shared Security Server
   * - 100K FCFA/month
   */
  STANDARD = 'STANDARD',

  /**
   * Medium tier for active agencies
   * - Max 50 services
   * - 500,000 API calls/month
   * - Dedicated Security Server
   * - 200K FCFA/month
   */
  MEDIUM = 'MEDIUM',

  /**
   * Large tier for high-volume agencies
   * - Max 100 services
   * - 2,000,000 API calls/month
   * - Dedicated Security Server with backup
   * - 400K FCFA/month
   */
  LARGE = 'LARGE',
}

// ============================================================================
// Tenant Metadata Interface
// ============================================================================

/**
 * Metadata for a tenant in the X-Road Control Tower.
 * Contains tenant type, hosting configuration, and service limits.
 */
export interface TenantMetadata {
  /**
   * Unique tenant identifier (from multi-tenant framework)
   */
  tenantId: string;

  /**
   * Tenant name (organization name)
   */
  tenantName?: string;

  /**
   * Type of tenant (determines UI and permissions)
   */
  tenantType: TenantType;

  /**
   * Parent tenant ID (for subsystems and hosted members)
   * - SUBSYSTEM_HOSTED/INTERNAL: Parent organization
   * - MEMBER_HOSTED/LIGHT: SENUM (Central Authority)
   * - CENTRAL_AUTHORITY/MEMBER_SELF_MANAGED: null
   */
  parentTenantId?: string | null;

  /**
   * Hosting tier (for hosted tenants only)
   * - Required for MEMBER_HOSTED and MEMBER_HOSTED_LIGHT
   * - null for CENTRAL_AUTHORITY and MEMBER_SELF_MANAGED
   */
  hostingTier?: HostingTier | null;

  /**
   * Maximum number of services this tenant can publish
   * - Enforced for hosted tenants
   * - null or -1 for unlimited (self-managed)
   */
  maxServices?: number | null;

  /**
   * Maximum API calls per month
   * - Enforced for hosted tenants
   * - null or -1 for unlimited (self-managed)
   */
  maxApiCallsPerMonth?: number | null;

  /**
   * X-Road member identifier (SN-GOV/GOV/1234fin)
   * Format: {instance}/{memberClass}/{memberCode}
   * - Required for members (not subsystems)
   * - null for CENTRAL_AUTHORITY
   */
  xroadMemberIdentifier?: string | null;

  /**
   * X-Road subsystem code (for subsystems only)
   * - Required for SUBSYSTEM_HOSTED/INTERNAL
   * - null for members
   */
  xroadSubsystemCode?: string | null;

  /**
   * Is this tenant currently active?
   */
  isActive?: boolean;

  /**
   * Tenant creation timestamp
   */
  createdAt?: string;

  /**
   * Last updated timestamp
   */
  updatedAt?: string;
}

// ============================================================================
// UI Helper Types
// ============================================================================

/**
 * Labels for tenant types are i18n keys: t(`xroad.tenantTypes.${TenantType}`).
 * See fr.json / en.json under xroad.tenantTypes.*.
 */

/**
 * Color scheme for tenant types (for badges and UI indicators)
 */
export const TenantTypeColors: Record<TenantType, string> = {
  [TenantType.CENTRAL_AUTHORITY]: 'blue', // Authority - institutional
  [TenantType.MEMBER_SELF_MANAGED]: 'green', // Self-managed - independence
  [TenantType.MEMBER_HOSTED]: 'orange', // Hosted - partnership
  [TenantType.MEMBER_HOSTED_LIGHT]: 'gray', // Light - entry level
  [TenantType.SUBSYSTEM_HOSTED]: 'purple', // Subsystem - specialized
  [TenantType.SUBSYSTEM_INTERNAL]: 'slate', // Internal - departmental
};

/**
 * Tailwind CSS classes for tenant type badges
 */
export const TenantTypeBadgeClasses: Record<TenantType, string> = {
  [TenantType.CENTRAL_AUTHORITY]: 'bg-blue-100 text-blue-800 border-blue-300',
  [TenantType.MEMBER_SELF_MANAGED]: 'bg-green-100 text-green-800 border-green-300',
  [TenantType.MEMBER_HOSTED]: 'bg-orange-100 text-orange-800 border-orange-300',
  [TenantType.MEMBER_HOSTED_LIGHT]: 'bg-gray-100 text-gray-800 border-gray-300',
  [TenantType.SUBSYSTEM_HOSTED]: 'bg-purple-100 text-purple-800 border-purple-300',
  [TenantType.SUBSYSTEM_INTERNAL]: 'bg-slate-100 text-slate-800 border-slate-300',
};

/**
 * Labels for hosting tiers are i18n keys: t(`xroad.hostingTiers.${HostingTier}`).
 * See fr.json / en.json under xroad.hostingTiers.*.
 */

/**
 * Service limits by hosting tier
 */
export const HostingTierLimits: Record<HostingTier, { maxServices: number; maxApiCallsPerMonth: number }> = {
  [HostingTier.LIGHT]: { maxServices: 5, maxApiCallsPerMonth: 10_000 },
  [HostingTier.STANDARD]: { maxServices: 20, maxApiCallsPerMonth: 100_000 },
  [HostingTier.MEDIUM]: { maxServices: 50, maxApiCallsPerMonth: 500_000 },
  [HostingTier.LARGE]: { maxServices: 100, maxApiCallsPerMonth: 2_000_000 },
};

/**
 * Type guard to check if a tenant type requires a hosting tier
 */
export function requiresHostingTier(tenantType: TenantType): boolean {
  return (
    tenantType === TenantType.MEMBER_HOSTED ||
    tenantType === TenantType.MEMBER_HOSTED_LIGHT
  );
}

/**
 * Type guard to check if a tenant type is a subsystem
 */
export function isSubsystem(tenantType: TenantType): boolean {
  return (
    tenantType === TenantType.SUBSYSTEM_HOSTED ||
    tenantType === TenantType.SUBSYSTEM_INTERNAL
  );
}

/**
 * Type guard to check if a tenant can manage their own Security Server
 */
export function canManageSecurityServer(tenantType: TenantType): boolean {
  return tenantType === TenantType.MEMBER_SELF_MANAGED;
}

/**
 * Type guard to check if a tenant has central authority privileges
 */
export function isCentralAuthority(tenantType: TenantType): boolean {
  return tenantType === TenantType.CENTRAL_AUTHORITY;
}
